import { useCallback, useEffect, useState } from 'react';
import {
  applyTheme,
  DEFAULT_THEME,
  type DesignTheme,
} from '../lib/design/theme';
import {
  fetchActiveTheme,
  listPresets,
  writeCachedTheme,
  type DesignPreset,
} from '../lib/design/store';

/**
 * Loads the active design theme from the DB on mount and applies it. Used once,
 * high in the tree, so the whole app re-themes when an admin switches presets.
 */
export function useActiveDesignTheme(): void {
  useEffect(() => {
    let cancelled = false;
    void fetchActiveTheme().then((theme) => {
      if (cancelled || !theme) return;
      applyTheme(theme);
      writeCachedTheme(theme);
    });
    return () => {
      cancelled = true;
    };
  }, []);
}

interface DesignAdminState {
  presets: DesignPreset[];
  active: DesignTheme;
  loading: boolean;
  reload: () => Promise<void>;
}

/**
 * Admin-facing state: the preset library plus the currently-running theme.
 * Mutations (apply / import / delete) live in lib/design/store; call reload()
 * after them to refresh and re-apply.
 */
export function useDesignAdmin(): DesignAdminState {
  const [presets, setPresets] = useState<DesignPreset[]>([]);
  const [active, setActive] = useState<DesignTheme>(DEFAULT_THEME);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    const list = await listPresets();
    setPresets(list);
    const activePreset = list.find((p) => p.is_active);
    const theme = activePreset?.theme ?? DEFAULT_THEME;
    setActive(theme);
    applyTheme(theme);
    writeCachedTheme(theme);
    setLoading(false);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { presets, active, loading, reload };
}
